import _ from 'underscore';
import Promise from 'bluebird';
import { isPersonalizedOfferAvailable, logCampaignPersonalizedOffers } from '@bingads-webui-campaign/personalized-offer-shared';
import { CashbackSettings } from '@bingads-webui-campaign/cashback-settings';
import { BackboneSchema as backboneSchema } from '@bingads-webui/backbone-schema';
import { DependencyResolver } from '@bingads-webui-campaign/dependency-resolver';
import { getBulkErrorsLogMessage, getProductData, CampaignSubTypes } from '@bingads-webui-campaign/audience-campaign-shared';
import { withUiBlock } from '@bingads-webui/uiblocker';
import { queryify } from '@bingads-webui/url-util';
import { CrudAudienceCampaign } from './view';

/**
 * CrudAudienceShoppingCampaign class that extends {CrudAudienceCampaign} class
 * The class defines the section of the setting Campaign
 *
 * @class CrudAudienceShoppingCampaign
 * @extends {CrudAudienceCampaign}
 */
export class CrudAudienceShoppingCampaign extends CrudAudienceCampaign {
  /**
   * Initializes the {CrudAudienceShoppingCampaign} class.
   *
   * @param {Object} options - options for initializing the class.
   * @param {Object} options.viewContext = viewContext for the View.
   * @returns {undefined}
   */
  initialize(options) {
    if (options && options.perfMarker) {
      options.perfMarker.willInit();
    }
    this.renderMarker;
    this.viewContext = options.viewContext;
    this.wizardElement = options.wizardElement;
    this.i18n = DependencyResolver.get('component/i18n/loader!campaignui/feature/audience-campaign');
    this.i18nErrors = DependencyResolver.get('component/api-errors-campaign/index');
    this.productData = options.data;
    this.feedData = options.feedData;
    this.isNextButtonEnabled = (
      !(this.viewContext.isReadOnlyUser || this.viewContext.CurrentAccountInActive)
    );

    const config = DependencyResolver.get('component/config/index');
    this.permissions = config.get('Permissions') || {};
    this.isEnabledDynamicFeedCampaign = this.permissions.IsEnabledForDynamicFeedCampaign;
    this.isEnhancedPAFilterP2Enabled = Boolean(_.result(config.get('Permissions'), 'IsEnhancedPAFilterP2Enabled'));

    const schema = DependencyResolver.get('$/schema!dynamicdb/ShoppingCampaign');
    const SchemaModel = backboneSchema(
      schema,
      this.i18n,
      ['Name', 'CampaignType', 'TimeZone', 'Languages', 'ProviderId']
    );

    this.model = new SchemaModel();
    this.logScenario = options.logScenario;
    this.activityLogger = this.logScenario.createCampaignSettings.create();
    this.perfMarker = options.perfMarker;

    super.initControls();
    this.initShoppingSettingsView();

    this.shouldShowCashbackSettings = options.isForVNext && isPersonalizedOfferAvailable(
      this.permissions,
      options.audienceCampaignType,
      options.audienceCampaignSubType
    );
    this.options = options;

    this.isCopyCampaignSettingsAudienceCampaignEnabled = _.result(this.permissions, 'IsCopyCampaignSettingsAudienceCampaignEnabled', false);
    if (this.isCopyCampaignSettingsAudienceCampaignEnabled) {
      const CopyCampaignSettings = DependencyResolver.get('crud-campaign/view/copy-campaign-settings');
      this.copySettings = new CopyCampaignSettings({
        scenario: 'copy-audience-campaign-settings',
        activityLogger: this.activityLogger,
      });
      this.copySettings.on('copy.campaign', this.copyFromCampaign.bind(this));
    }

    this.initCashbackSettingsView();
  }

  initCashbackSettingsView() {
    if (this.shouldShowCashbackSettings && this.options.deps) {
      this.cashbackSettingsView = new CashbackSettings({
        deps: { ...this.options.deps, i18n: this.i18n },
        isCreationScenario: true,
      });

      this.shoppingSettingsView.on('feedChanged', (feedId) => {
        if (feedId) {
          this.cashbackSettingsView.viewModel.isEnablePersonalizedOffersDisabled(true);
          this.cashbackSettingsView.viewModel.isEnablePersonalizedOffersChecked(false);
        } else {
          this.cashbackSettingsView.viewModel.isEnablePersonalizedOffersDisabled(false);
        }
      });
    }
  }

  /**
   * Renders the view
   *
   * @returns {View} returns the view itself
   */
  render() {
    if (this.options && this.options.perfMarker) {
      this.renderMarker = this.perfMarker.createChild('RenderShoppingCampaignCrud');
      this.renderMarker.willInit();
    }
    this.perfMarker.willRender();

    super.render();

    this.$('.shopping-settings-container').append(this.shoppingSettingsView.render().$el);

    if (this.cashbackSettingsView) {
      this.$('.cashback-settings-container').empty().append(this.cashbackSettingsView.render().$el);
    }
    return this;
  }

  validate() {
    const isCashbackSettingsValid = !this.cashbackSettingsView ||
      this.cashbackSettingsView.validate();

    return this.shoppingSettingsView.validate() && super.validate() && isCashbackSettingsValid;
  }

  /**
   * Get the data that is used for the parameter for saving.
   *
   * @returns {Object} - object representing the data for the campaign to be saved.
   */
  getData() {
    const params = super.getData();

    if (this.isEnabledDynamicFeedCampaign) {
      params.FeedId = this.shoppingSettingsView.viewModel.FeedId();
      if (!params.FeedId) {
        params.ProviderId = this.shoppingSettingsView.viewModel.ProviderId();
      }
    } else {
      params.ProviderId = this.shoppingSettingsView.viewModel.ProviderId();
    }

    params.CampaignSubType = params.FeedId
      ? CampaignSubTypes.AudienceDynamicFeedBased
      : CampaignSubTypes.AudienceFeedBased;

    if (this.shoppingSettingsView.hasFilteredProducts()) {
      params.Priority = 'Low';
      params.LocalInventoryAdsEnabled = false;

      params.CampaignProductScope = _.defaults(this.shoppingSettingsView.getProductScope(), {
        CampaignName: params.Name,
      });

      if (this.productData.CampaignCriterionsData && this.productData.CampaignCriterionsData.Id) {
        params.CampaignProductScope.Id = this.productData.CampaignCriterionsData.Id;
      }
    }

    if (!this.viewContext.currentCampaign) {
      params.AdScheduleUseSearcherTimeZone = false;
    }

    if (this.cashbackSettingsView) {
      _.extend(params, this.cashbackSettingsView.getValues());
    }

    return params;
  }

  /**
   * Save the campaign with the given setting.
   *
   * @returns {Promise} - returns promise with a return boolean value indicating
   * if the save is succesful or not.
   */
  save() {
    if (this.shoppingSettingsView.hasFilteredProducts() &&
      this.shoppingSettingsView.filterDataComesFrom === 'NewComponent') {
      this.shoppingSettingsView.clickApplyButton();
    }

    if (!this.validate()) {
      return Promise.resolve(false);
    }

    const params = this.getData();

    let createOrUpdateTask;

    const jsdata = DependencyResolver.get('component/js-data-campaign/index');

    if (this.viewContext.currentCampaign) {
      params.Id = this.viewContext.currentCampaign.Id;
      createOrUpdateTask =
        jsdata.ShoppingCampaign.update(params.Id, params, { adapter: 'bulk', cacheResponse: false });
    } else {
      // when isEnabledDeprecateManualCpcForAudienceCampaign is enabled, we create the campaign with BiddingScheme eCpc
      // because we want to deprecate mCpc for audience campaign
      // the default BiddingScheme will be eCpc for audience campaign except for video/brandawareness audience campaigns
      // if UI pass mCpc to MT, MT will convert it into eCpc
      if (params.CampaignType === 'Audience' && _.result(this.permissions, 'IsEnabledDeprecateManualCpcForAudienceCampaign', false)) {
        params.BiddingScheme = {
          Type: 'EnhancedCpc',
        };
      }
      createOrUpdateTask =
        jsdata.ShoppingCampaign.create(params, { adapter: 'bulk', cacheResponse: false });
    }

    this.errorsView.removeErrors();

    return createOrUpdateTask.then(data => getProductData({
      campaignId: data.Id,
      getCampaignCriterionsOnly: true,
    }).then((productData) => {
      if (productData && this.productData) {
        this.productData.CampaignCriterionsData = productData.CampaignCriterionsData;
      }

      if (_.isFinite(data.Id)) {
        const campaign = data;
        campaign.CampaignCashback = {
          MonthlyBudget: campaign.CashbackMonthlyBudget,
          Percent: campaign.CashbackPercent,
          Scope: campaign.CashbackScope,
          CouponsEnabled: campaign.CouponsEnabled,
          PersonalizedOffersEnabled: campaign.PersonalizedOffersEnabled,
        };

        logCampaignPersonalizedOffers(
          this.cashbackSettingsView,
          this.activityLogger.signal.bind(this.activityLogger),
          data
        );

        campaign.CampaignSettings = [{
          ProviderId: campaign.ProviderId,
          FeedId: campaign.FeedId,
        }];

        this.viewContext.currentCampaign = campaign;

        return true;
      }

      return false;
    }))
      .catch((response) => {
        if (response && response.entity) {
          const hasErrors = response && !_.isUndefined(response.errors);

          if (hasErrors) {
            if (response.errors.length) {
              this.errorsView.setErrors(this.i18nErrors.getBulkApiErrors(response.errors));

              this.activityLogger.trace(getBulkErrorsLogMessage(response.errors));
              _.each(response.errors, (error) => {
                this.activityLogger.signal(error.Error, 'audience campaign creation api error');
              });
            }
          } else {
            // Unexpected case. Log Error.
            this.activityLogger.error(JSON.stringify(response));
          }
        } else {
          // Ajax error [400] - RowCountExceeded
          this.errorsView.setErrors(this.i18nErrors.getODataErrors(response.errors));
          _.each(response.errors, (error) => {
            this.activityLogger.signal(error.Error, 'audience campaign creation api error');
          });
        }

        return false;
      })
      .finally(withUiBlock(this.wizardElement, this.i18n.getString('Loading')));
  }

  willShow() {
    super.willShow();
    if (this.viewContext.currentCampaign) {
      if (this.isEnabledDynamicFeedCampaign) {
        this.shoppingSettingsView.renderCatagrizedOptionsList({
          disabled: true,
        });
      } else {
        this.shoppingSettingsView.disableStoreSelection();
      }
    }
    this.perfMarker.done();
    // As we show the first section of the wizard, we consider the whole wizard done rendered.

    if (this.perfMarker.parentMarker) {
      this.perfMarker.parentMarker.done();
    }
    if (this.options && this.options.perfMarker && this.renderMarker) {
      this.renderMarker.done();
    }
  }

  /**
   * Initialize shopping settings view
   *
   * @private
   * @returns {undefined}
   */
  initShoppingSettingsView() {
    const ShoppingSettingsView = DependencyResolver.get('campaignui/feature/shopping-campaign/view/campaign-creation/shopping-settings');

    this.shoppingSettingsView = new ShoppingSettingsView({
      data: this.productData,
      feedData: this.feedData,
      model: this.setting,
      campaignType: 'Audience',
      campaignSubType: 'AudienceFeedBased',
      activityLogger: this.activityLogger,
    });
  }

  copyFromCampaign(source) {
    const noExistingCampaign = !this.viewContext.currentCampaign;
    super.copyFromCampaign(source);
    const campaign = _.first(source);
    this.activityLogger.signal(`Copy setting from campaign: ${campaign.Name} ${campaign.Id}`, 'Audience Copy Campaign Setting');

    // copy Personalized promotions(cashback setting)
    const cashBackSettings = _.result(campaign, 'CampaignCashback');
    if (cashBackSettings) {
      this.cashbackSettingsView.setValues({
        PersonalizedOffersEnabled: cashBackSettings.PersonalizedOffersEnabled,
        CashbackMonthlyBudget: cashBackSettings.MonthlyBudget,
        CashbackPercent: cashBackSettings.Percent,
        CashbackScope: cashBackSettings.Scope,
        CouponsEnabled: cashBackSettings.CouponsEnabled,
      });
    }

    const copyWhiteList = [
      'AudienceDynamicFeedBased',
      'AudienceFeedBased',
    ];

    if (_.contains(copyWhiteList, campaign.CampaignSubType) && noExistingCampaign) {
      let feedId;
      const getTreeInfo = this.isEnhancedPAFilterP2Enabled && (campaign.CampaignSubType === 'AudienceFeedBased');
      if (campaign.CampaignSubType === 'AudienceDynamicFeedBased') {
        feedId = _.result(campaign.CampaignSettings[0], 'FeedId');
      } else {
        feedId = _.result(campaign.CampaignSettings[0], 'ProviderId');
      }
      this.shoppingSettingsView.refreshCategorizedOptionsList(feedId);
      this.getProductData(_.result(campaign, 'Id'), _.first(campaign.CampaignSettings[0]), getTreeInfo)
        .then((productData) => {
          this.shoppingSettingsView.refreshData(campaign, productData);
        })
        .finally(withUiBlock());
    } else if (noExistingCampaign) {
      this.shoppingSettingsView.viewModel.selectAllProducts(true);
    }
  }

  // todo: private\campaign-legacy\packages\shopping-campaign\view\campaign-creation\index.js
  // Move getProductData's code there and this code to common area after permissions modifications.
  getProductData(campaignId, setting, getTreeInfo) {
    const jsdata = DependencyResolver.get('component/js-data-campaign/index');
    const state = DependencyResolver.get('component/state/index');

    let url = queryify('/Campaign/NewCampaign/GetProductScope', {
      aid: state.get('account_id'),
      cid: state.get('customer_id'),
      uid: state.get('user_id'),
    });

    const urlQueryStringObject = {
      campaignId,
      salesCountry: _.result(setting, 'SalesCountry'),
    };

    url = queryify(url, urlQueryStringObject);

    if (this.shouldShowPlaceholderStoreIfExists) {
      url = `${url}&storeTypes=placeholder`;
    }

    if (getTreeInfo) {
      url = queryify(url, {
        getCategoryFullData: true,
        getProductTypeFullData: true,
      });
    }

    const params = {
      url,
      adapter: 'legacy',
      bypassCache: true,
      cacheResponse: false,
    };

    return jsdata.Product.find('MetaData', params)
      // Ui blocker is added by caller function
      .catch(() => {
        this.viewModel.errors.add();
      });
  }
}
